import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

function InvoiceDetailModal({ invoice, onClose, onRefresh }) {
  const [sendingToXero, setSendingToXero] = useState(false);

  const formatCurrency = (amount) => {
    return 'R' + parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoice.id}/pdf`);
      if (!res.ok) throw new Error('Failed to generate PDF');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF');
    }
  };

  const handleSendToXero = async () => {
    if (!window.confirm('Send this invoice to Xero as a draft invoice?')) {
      return;
    }

    setSendingToXero(true);

    try {
      const res = await fetch(`${API_URL}/api/xero/send-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId: invoice.id })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send invoice to Xero');
      }

      alert(`Invoice successfully sent to Xero!\nXero Invoice ID: ${data.xeroInvoiceId}`);
      
      // Refresh invoice list
      if (onRefresh) {
        onRefresh();
      }
      
      onClose();
    } catch (err) {
      console.error('Error sending to Xero:', err);
      
      if (err.message.includes('Not connected to Xero')) {
        alert('Please connect to Xero first. Go to Settings to set up the connection.');
      } else {
        alert(`Failed to send invoice to Xero: ${err.message}`);
      }
    } finally {
      setSendingToXero(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '32px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'start',
          marginBottom: '24px'
        }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 700, color: '#1976d2' }}>
              {invoice.invoiceNumber}
            </h2>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Created: {formatDate(invoice.createdAt)}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px'
            }}
          >
            ×
          </button>
        </div>

        {/* Customer Info */}
        <div style={{
          background: '#f9f9f9',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '12px' }}>
            BILL TO:
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            {invoice.customerName}
          </div>
          {invoice.customerAddress && (
            <div style={{ fontSize: '14px', color: '#666', whiteSpace: 'pre-line', marginBottom: '8px' }}>
              {invoice.customerAddress}
            </div>
          )}
          {invoice.customerEmail && (
            <div style={{ fontSize: '14px', color: '#666' }}>
              Email: {invoice.customerEmail}
            </div>
          )}
          {invoice.customerPhone && (
            <div style={{ fontSize: '14px', color: '#666' }}>
              Phone: {invoice.customerPhone}
            </div>
          )}
        </div>

        {/* Line Items */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#666', marginBottom: '12px' }}>
            LINE ITEMS:
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Code
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Description
                </th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Qty
                </th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Unit Price
                </th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems.map((item) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {item.code || '-'}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {item.description}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>
                    {item.quantity}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600 }}>
                    {formatCurrency(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{
          background: '#f9f9f9',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Subtotal:</span>
            <span style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(invoice.subtotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>VAT (15%):</span>
            <span style={{ fontSize: '14px', fontWeight: 600 }}>{formatCurrency(invoice.vatAmount)}</span>
          </div>
          <div style={{ 
            borderTop: '2px solid #e0e0e0', 
            paddingTop: '12px',
            display: 'flex', 
            justifyContent: 'space-between' 
          }}>
            <span style={{ fontSize: '18px', fontWeight: 700 }}>TOTAL:</span>
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#1976d2' }}>
              {formatCurrency(invoice.total)}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleDownloadPDF}
            style={{
              padding: '12px 24px',
              background: '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            📄 Download PDF
          </button>
          {invoice.status === 'DRAFT' && (
            <button
              onClick={handleSendToXero}
              disabled={sendingToXero}
              style={{
                padding: '12px 24px',
                background: sendingToXero ? '#ccc' : '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: sendingToXero ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: sendingToXero ? 0.7 : 1
              }}
            >
              {sendingToXero ? '🔄 Sending...' : '📤 Send to Xero'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvoiceDetailModal;
